import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Typography, Grid, Card, CardContent, CardMedia, 
  CardActionArea, Box, Button, TextField, CircularProgress,
  Divider, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, FormControlLabel,
  Switch
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { API_URL } from '../config';
import Mermaid from './Mermaid';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';

const MessageBubble = styled(Paper)(({ theme, sender }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  maxWidth: '80%',
  borderRadius: 16,
  backgroundColor: sender === 'customer' ? '#f1f0f0' : '#d1e7ff',
  alignSelf: sender === 'customer' ? 'flex-start' : 'flex-end',
}));

const StageInfo = styled(Box)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  fontStyle: 'italic',
}));

const ConversationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '500px',
  overflowY: 'auto',
  padding: theme.spacing(2),
  backgroundColor: '#f9f9f9',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const ScenarioCard = styled(Card)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[3],
  },
  border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
  height: '100%',
}));

// Add a new styled component for the stage transition information
const StageTransition = styled(Box)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  padding: theme.spacing(1, 2),
  margin: theme.spacing(1, 0),
  textAlign: 'center',
  backgroundColor: '#f0f8ff',
  borderRadius: theme.shape.borderRadius,
  border: '1px dashed #ccc',
  alignSelf: 'center',
  maxWidth: '80%',
}));

// Add a new styled component for the prompt display
const PromptDisplay = styled(Box)(({ theme }) => ({
  fontSize: '0.75rem',
  padding: theme.spacing(1.5),
  margin: theme.spacing(1, 0),
  backgroundColor: '#f5f5f5',
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #e0e0e0',
  alignSelf: 'stretch',
  maxWidth: '100%',
  whiteSpace: 'pre-wrap',
  fontFamily: 'monospace',
  overflowX: 'auto',
}));

// Add a styled component for the prompts display
const PromptsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: '#f9f9f9',
  borderRadius: theme.shape.borderRadius,
  border: '1px solid #e0e0e0',
}));

const PromptCard = styled(Paper)(({ theme, type }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: type === 'advisor_prompt' ? '#e3f2fd' : '#fff3e0',
  whiteSpace: 'pre-wrap',
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  overflowX: 'auto',
}));

// Create a container for each message and its button
const MessageContainer = styled(Box)(({ theme, sender }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: sender === 'customer' ? 'flex-start' : 'flex-end',
  marginBottom: theme.spacing(1),
  position: 'relative',
  width: '100%',
}));

// Style for the button placed below the message
const PromptButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  padding: '2px 8px',
  minWidth: 'unset',
  fontSize: '0.7rem',
  textTransform: 'none',
}));

// Create a styled component for the prompt popup
const PromptPopup = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  zIndex: 1000,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  maxHeight: '80vh',
  overflowY: 'auto',
  padding: theme.spacing(3),
  backgroundColor: '#fff',
  boxShadow: theme.shadows[5],
}));

function MockConversation() {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [numExchanges, setNumExchanges] = useState(1);
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const conversationRef = useRef(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [scenarioDetail, setScenarioDetail] = useState(null);
  const [flowchartContent, setFlowchartContent] = useState('');
  const [showPrompts, setShowPrompts] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [visiblePrompt, setVisiblePrompt] = useState(null);
  const [promptType, setPromptType] = useState(null);

  // Get the base URL for API calls
  const getBaseUrl = () => {
    return API_URL;
  };

  useEffect(() => {
    fetchScenarios();
  }, []);

  useEffect(() => {
    if (conversationRef.current && showConversation) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [conversation, showConversation]);

  const fetchScenarios = async () => {
    try {
      const baseUrl = getBaseUrl();
      const response = await axios.get(`${baseUrl}/api/scenarios`);
      setScenarios(response.data.scenarios);
    } catch (error) {
      console.error('Error loading scenarios:', error);
    }
  };

  const handleScenarioSelect = async (scenario) => {
    setSelectedScenario(scenario.id);
    setScenarioDetail(scenario);
    setFlowchartContent(''); // Reset flowchart content
    
    // Load flowchart for the selected scenario
    try {
      const response = await axios.get(`/flowcharts/${scenario.id}_agent_flow.md`);
      if (response.data) {
        // Extract the mermaid code from the markdown file
        const mermaidMatch = response.data.match(/```mermaid\n([\s\S]*?)```/);
        if (mermaidMatch && mermaidMatch[1]) {
          setFlowchartContent(mermaidMatch[1]);
        }
      }
    } catch (error) {
      console.error(`Error loading flowchart for scenario ${scenario.id}:`, error);
      // Just continue without a flowchart if there's an error
    }
    
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
  };

  const handleConfirmSelection = () => {
    setDetailDialogOpen(false);
  };

  const handleGenerateConversation = async () => {
    if (!selectedScenario) {
      alert('Please select a scenario first.');
      return;
    }

    setLoading(true);
    setShowConversation(false);

    try {
      const baseUrl = getBaseUrl();
      const response = await axios.post(`${baseUrl}/api/mock-conversation`, {
        scenario_id: selectedScenario,
        num_exchanges: numExchanges,
      });

      const data = response.data;
      
      setConversation(data.conversation);
      setPrompts(data.prompts || []); // Store the prompts
      setShowConversation(true);
    } catch (error) {
      console.error('Error generating conversation:', error);
      alert('Failed to generate conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (conversation.length === 0) return;
    
    const text = conversation.map(msg => 
      `${msg.role.charAt(0).toUpperCase() + msg.role.slice(1)}: ${msg.content}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(text)
      .then(() => {
        alert('Conversation copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy:', err);
      });
  };

  const getSelectedScenarioDetails = () => {
    return scenarios.find(s => s.id === selectedScenario);
  };

  const showPromptDialog = (index, type) => {
    setVisiblePrompt(index);
    setPromptType(type);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        模擬對話
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                設定
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="對話次數"
                  type="number"
                  value={numExchanges}
                  onChange={(e) => setNumExchanges(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                  InputProps={{ inputProps: { min: 1, max: 20 } }}
                  sx={{ width: 200 }}
                />
                <Button 
                  variant="contained" 
                  onClick={handleGenerateConversation}
                  disabled={!selectedScenario || loading}
                >
                  生成對話
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Select a Scenario
          </Typography>
          <Grid container spacing={1}>
            {scenarios.map((scenario) => (
              <Grid item xs={12} sm={4} md={3} key={scenario.id}>
                <ScenarioCard 
                  selected={selectedScenario === scenario.id}
                  onClick={() => handleScenarioSelect(scenario)}
                >
                  <CardActionArea sx={{ height: '100%' }}>
                    <Box sx={{ display: 'flex', p: 1, alignItems: 'center' }}>
                      <CardMedia
                        component="img"
                        sx={{ width: 40, height: 40, borderRadius: '50%', mr: 1 }}
                        image={scenario.image}
                        alt={scenario.name}
                      />
                      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Typography variant="subtitle1" noWrap>
                          {scenario.name}, {scenario.age}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {scenario.title}
                        </Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </ScenarioCard>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Scenario Detail Dialog */}
        <Dialog 
          open={detailDialogOpen} 
          onClose={handleCloseDetailDialog}
          maxWidth="md"
          fullWidth
        >
          {scenarioDetail && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CardMedia
                    component="img"
                    sx={{ width: 50, height: 50, borderRadius: '50%', mr: 2 }}
                    image={scenarioDetail.image}
                    alt={scenarioDetail.name}
                  />
                  <Box>
                    <Typography variant="h6">
                      {scenarioDetail.name}, {scenarioDetail.age}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {scenarioDetail.title}
                    </Typography>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Description</Typography>
                    <Typography variant="body2" paragraph style={{ whiteSpace: 'pre-line' }}>
                      {scenarioDetail.description}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Background</Typography>
                    <Typography variant="body2" paragraph style={{ whiteSpace: 'pre-line' }}>
                      {scenarioDetail.background}
                    </Typography>
                  </Grid>
                  
                  {scenarioDetail.stage && scenarioDetail.stage.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>Stages</Typography>
                      <List dense>
                        {scenarioDetail.stage.map((stage, index) => (
                          <ListItem key={index}>
                            <ListItemText 
                              primary={stage.name} 
                              secondary={stage.description} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                  
                  {flowchartContent && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Mermaid chart={flowchartContent} />
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDetailDialog}>Cancel</Button>
                <Button onClick={handleConfirmSelection} variant="contained" color="primary">
                  Select This Scenario
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {loading && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Generating conversation...
            </Typography>
          </Grid>
        )}

        {showConversation && (
          <Grid item xs={12}>
            <Card>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="h6">
                  {getSelectedScenarioDetails()?.name} - {getSelectedScenarioDetails()?.title}
                </Typography>
                <Box>
                  <IconButton onClick={copyToClipboard} title="Copy to clipboard">
                    <ContentCopyIcon />
                  </IconButton>
                </Box>
              </Box>
              <Divider />
              <ConversationContainer ref={conversationRef}>
                {conversation.map((msg, index) => (
                  <MessageContainer key={index} sender={msg.role.toLowerCase()}>
                    {/* Add stage transition information after each advisor message if available */}
                    {msg.role === 'advisor' && msg.current_stage && msg.next_stage && (
                      <StageTransition>
                        Current: {msg.current_stage} → Next: {msg.next_stage}
                      </StageTransition>
                    )}
                    <MessageBubble sender={msg.role.toLowerCase()}>
                      <Typography variant="body1">
                        {msg.content}
                      </Typography>
                    </MessageBubble>
                    
                    {/* Add prompt button for advisor messages */}
                    {msg.role === 'advisor' && msg.prompt && (
                      <PromptButton 
                        variant="outlined"
                        size="small" 
                        color="primary"
                        startIcon={<CodeIcon fontSize="small" />}
                        onClick={() => showPromptDialog(index, 'advisor')}
                      >
                        Prompt
                      </PromptButton>
                    )}
                    
                    {/* Add prompt button for customer messages */}
                    {msg.role === 'customer' && msg.customer_prompt && (
                      <PromptButton 
                        variant="outlined"
                        size="small" 
                        color="secondary"
                        startIcon={<CodeIcon fontSize="small" />}
                        onClick={() => showPromptDialog(index, 'customer')}
                      >
                        Prompt
                      </PromptButton>
                    )}
                  </MessageContainer>
                ))}
              </ConversationContainer>
            </Card>
          </Grid>
        )}

        {showPrompts && prompts.length > 0 && (
          <Grid item xs={12}>
            <PromptsContainer>
              <Typography variant="h6" gutterBottom>
                LLM Prompts
              </Typography>
              {prompts.map((prompt, index) => (
                <PromptCard key={index} type={prompt.role}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {prompt.role === 'advisor_prompt' ? 'Advisor Generation Prompt' : 'Customer Generation Prompt'}
                  </Typography>
                  {prompt.content}
                </PromptCard>
              ))}
            </PromptsContainer>
          </Grid>
        )}

        {/* Prompt popup dialog */}
        {visiblePrompt !== null && conversation[visiblePrompt] && (
          <Dialog
            open={visiblePrompt !== null}
            onClose={() => {setVisiblePrompt(null); setPromptType(null);}}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {promptType === 'advisor' 
                    ? "Advisor Response Prompt" 
                    : "Customer Response Prompt"}
                </Typography>
                <IconButton onClick={() => {setVisiblePrompt(null); setPromptType(null);}} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ 
                whiteSpace: 'pre-wrap', 
                fontFamily: 'monospace', 
                fontSize: '0.9rem',
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                overflowX: 'auto'
              }}>
                {promptType === 'advisor' 
                  ? conversation[visiblePrompt].prompt 
                  : conversation[visiblePrompt].customer_prompt}
              </Box>
            </DialogContent>
          </Dialog>
        )}
      </Grid>
    </Container>
  );
}

export default MockConversation; 